/**
 * Tournament-wide settings: the first settings entity in this app.
 *
 * Deliberately generic: ROADMAP §3 (branding, sponsor strip) needs the same
 * row, so this is a bag of NAMED SECTIONS rather than a flat sheet of spirit
 * fields. `spirit` was the first; `stats` (leaderboards and awards) is the
 * second and cost one key, one parser and one form, which is the point.
 *
 * INVARIANT: settings are PUBLIC. `GET /api/settings` serves them with no
 * credential, because the board has to know whether spirit standings are
 * published and branding is public by nature. Never put a secret, a key, a
 * contact detail or anything else private in here: add a separate, gated
 * storage key instead. `publicSettings()` below is the one seam where that
 * split would happen, and it lists every exposed field explicitly so adding a
 * field is a deliberate act rather than a spread.
 */

/**
 * How much of the spirit data reaches the public board. A tournament director
 * picks one before the event; the choice is a real product decision and the
 * options are the three positions taken by the sources, plus off.
 *
 *   private  : nothing. Publishing live invites retaliation scoring at a small
 *               event where the same teams meet repeatedly.
 *   games    : per-game totals, both directions, and only once BOTH teams have
 *               submitted. This is what the WFDF Spirit Director Manual
 *               recommends: transparent enough that a team can correct course,
 *               with the running averages held back for the prize.
 *   standings: the table only. Average and per-category averages, no per-game
 *               breakdown, so nobody can read off who gave them what.
 *   full     : both, which is what WFDF's own results site does at WUCC.
 *
 * Comments are never public in ANY mode.
 */
import { DEFAULT_SLOTS, type DivisionSlots, type SlotSettings } from "./game-length";

export const SPIRIT_VISIBILITIES = ["private", "games", "standings", "full"] as const;
export type SpiritVisibility = (typeof SPIRIT_VISIBILITIES)[number];

export function isSpiritVisibility(v: unknown): v is SpiritVisibility {
  return typeof v === "string" && (SPIRIT_VISIBILITIES as readonly string[]).includes(v);
}

/**
 * Separators applied in order when two teams finish on the same average.
 *
 * WFDF publishes NO tiebreak, not in the 2025-2028 Appendix, not on the score
 * sheet, not in the Spirit Director Manual, which says only that the director
 * awards the prize to the highest average. WFDF's own results site lists tied
 * teams adjacent with no rank column at all. So the default chain is EMPTY and
 * ties are shown honestly; a director who needs exactly one prize winner picks
 * their own separators here rather than having this app invent one.
 */
export const SPIRIT_TIEBREAKS = [
  /** The higher tournament finish first (2026-09-11, the owner's rule): the
   *  bracket finish where a deciding game gave one, the table's rank on a
   *  pool-only division (`tournamentPlacements` in team-record.ts). A team
   *  without a finish sits below every team with one and level with the
   *  others without one. */
  "placement",
  "games",
  "fewestLowScores",
  "rules",
  "fouls",
  "attitude",
  "fairness",
  "communication",
] as const;
export type SpiritTiebreak = (typeof SPIRIT_TIEBREAKS)[number];

export function isSpiritTiebreak(v: unknown): v is SpiritTiebreak {
  return typeof v === "string" && (SPIRIT_TIEBREAKS as readonly string[]).includes(v);
}

export interface SpiritSettings {
  /** Off means the team form and the admin tab say so instead of 404ing. */
  enabled: boolean;
  visibility: SpiritVisibility;
  /** Scores RECEIVED before a team is ranked. 0 ranks everybody. */
  minGames: number;
  tiebreak: SpiritTiebreak[];
  /** Zone-less local string, same convention as `ScheduledGame.startAt`.
   *  Advisory only: a late score is still accepted, because chasing one down
   *  on Sunday evening is the normal case, not an exception. */
  deadline?: string;
  /** WFDF's self-score column is optional and explicitly excluded from
   *  rankings. Off by default: the current one-game sheet dropped it, and it
   *  is one more thing to tap on a phone. */
  selfScoring: boolean;
}

/**
 * Player statistics and the awards built on top of them.
 *
 * Everything here is a PRESENTATION decision, which is why it can live in the
 * public row: the numbers themselves are already public (`GET /api/stats`
 * serves every snapshot). The award SHORTLIST and its notes are not here and
 * must never be: they live behind `awards:manage` in their own storage key,
 * because a candidate list read off the public settings row before the closing
 * ceremony is exactly the campaigning problem that made spirit standings
 * default to private.
 */
export interface StatsSettings {
  /**
   * Team-games a player needs before the leaderboard RANKS them. They are
   * still listed below it: hiding a player who scored eight in one game is
   * dishonest in the other direction. 0 ranks everybody.
   */
  minGames: number;
  /**
   * Show a combined table across every division, in addition to (never
   * instead of) the per-division ones. Off by default: divisions differ in
   * pace, format and game count, so the combined table is a curiosity next to
   * the per-division ones, which are what actually gets awarded. Meaningless
   * with a single division, where the UI hides the control entirely.
   */
  combined: boolean;
  /**
   * Master switch for published award decisions. An award also carries its own
   * `announcedAt`: that is the per-award act a director performs on stage,
   * and this is the one tap that takes them all back down if something is
   * wrong. Both must be true for the board or the overlay to see a winner.
   */
  awardsPublic: boolean;
  /**
   * Rank the PUBLIC leaderboards per game instead of by totals.
   *
   * The fairness case: a team knocked out after pool play has fewer games
   * than the finalists, so on totals its best player mathematically cannot
   * win a leaderboard - not a rule anybody chose, just an artefact of the
   * bracket. Per game divides by the team's games (with `minGames` still
   * gating who is RANKED). Off by default: totals are what most events read
   * out. The admin's own leaders view can flip modes either way; this decides
   * what a spectator sees.
   */
  perGame: boolean;
}

/**
 * Which language a device shows when it has not been told otherwise.
 *
 * A tournament-level DEFAULT, never a translation of anything: no stored row,
 * no game note and no team name is ever written in a locale. This is one hint
 * for a spectator arriving by QR code with no preference of their own, and the
 * device's own choice always wins over it.
 *
 * Ukrainian is the built-in default because the events this runs are Ukrainian;
 * an organiser running an international event flips it here once.
 */
export { DEFAULT_SLOTS, type DivisionSlots, type SlotSettings };

import { DEFAULT_CONFIG, type ScheduledGame } from "./types";

/**
 * One division's game format, when it does not follow the tournament.
 *
 * A FULL snapshot, not a diff: the toggle that switches a division on seeds
 * every field from what the division plays today (exactly like the length
 * overrides above it), so an absent CAP here is a real answer - "this
 * division calls no cap" - and never "inherit the tournament's". Inherit is
 * spelled by the whole entry being absent.
 */
export interface DivisionFormat {
  /** Game-to-N points; null = time-only. */
  scoreTarget: number | null;
  timeoutsPerHalf: number;
  softCapMin?: number;
  hardCapMin?: number;
}

/**
 * The scorekeeper's prefills.
 *
 * DELIBERATELY NOT a `GameConfig`: there is no clock length here, because the
 * app already knows it - the game clock runs the game's playing time, and the
 * playing time per kind and per division lives one card up in `slots`. An 80
 * minute Open game opens with 80:00 on the clock without anybody typing 80,
 * and a division moved to 60 opens at 60:00 in the same edit (`playMinutes`
 * in game-length.ts is the reader). Clock mode stays tournament-wide: an
 * event is either timed or to-points; per-division clockMode has no ask.
 */
export interface FormatSettings {
  clockMode: "countdown" | "countup";
  scoreTarget: number | null;
  timeoutsPerHalf: number;
  softCapMin?: number;
  hardCapMin?: number;
  byDivision?: Record<string, DivisionFormat>;
}

export const LOCALES = ["uk", "en"] as const;
export type Locale = (typeof LOCALES)[number];

export function isLocale(v: unknown): v is Locale {
  return typeof v === "string" && (LOCALES as readonly string[]).includes(v);
}

export interface LocaleSettings {
  /** Shown to a device that has neither a ?lang= override nor a saved choice. */
  default: Locale;
}

/**
 * What `PUT /api/settings` carries: the sections the caller is actually
 * changing, and nothing else. The row used to be replace-semantics — every
 * writer had to restate the WHOLE thing — which made two admins seconds apart
 * a race: B's stale copy of A's section rode along with B's edit and quietly
 * reverted it, and a lean body reset every omitted section to defaults. The
 * Durable Object now merges present sections over the stored row; a form
 * sends only the section it owns.
 */
export interface SettingsPatch {
  spirit?: SpiritSettings;
  stats?: StatsSettings;
  locale?: LocaleSettings;
  slots?: Partial<SlotSettings>;
  format?: FormatSettings;
  overlay?: OverlaySettings;
  setup?: SetupSettings;
}

/**
 * The two setup steps that have no data to prove them (V2 Phase 4,
 * `shared/onboarding.ts`). Everything else on the checklist is computed from
 * the tournament's rows; these are the organiser saying "this is deliberate":
 * one division, so there is nothing to create; the default durations, so
 * there is nothing to change. Flags, not steps ticked by hand: the step is
 * still computed, from the flag OR the data.
 */
export interface SetupSettings {
  /** The event plays one division and the Divisions tab may stay empty. */
  singleDivision: boolean;
  /** The compiled-in durations are the event's own. */
  durationsConfirmed: boolean;
}

/**
 * The auto scene's two timers (`shared/overlay-scene.ts` reads them):
 * how long the final card holds after a finish, and how early the pregame
 * intro pre-empts the schedule. Settings rather than constants because they
 * are event rhythm: a tight 85-minute-slot weekend and a chill league night
 * want different numbers, and neither should cost a deploy.
 */
export interface OverlaySettings {
  /** Seconds the final card stays up after "final" before the schedule returns. */
  finalCardSec: number;
  /** Minutes before a configured game's start the intro card takes the stream. */
  pregameLeadMin: number;
}

export interface TournamentSettings {
  spirit: SpiritSettings;
  stats: StatsSettings;
  locale: LocaleSettings;
  /**
   * How long a game takes and how long the pitch is busy afterwards.
   *
   * A tournament has always known these numbers and nothing was ever asking
   * for them: a stored game has a start and no duration, so the checker
   * inferred a length from the median gap on a pitch and the schedule
   * assistant simply invented one. See `shared/game-length.ts`.
   */
  slots: SlotSettings;
  /**
   * What the scorekeeper's "Set up game" dialog is PREFILLED with: clock
   * mode, half length, the game-to target, timeouts, the caps.
   *
   * A tournament plays one format all weekend, and before this section the
   * dialog opened on compiled-in defaults - a volunteer at 08:55 either knew
   * this event plays to 13 or shipped the wrong scoreboard. Defaults only:
   * the dialog still edits per game, and a configured game keeps its own
   * `GameConfig` forever (changing this mid-event never rewrites a field).
   */
  format: FormatSettings;
  /** The overlay auto scene's timers: see `OverlaySettings`. */
  overlay: OverlaySettings;
  /** The organiser's two acknowledgements on the setup checklist. */
  setup: SetupSettings;
}

export const DEFAULT_SPIRIT_SETTINGS: SpiritSettings = {
  enabled: true,
  visibility: "private",
  minGames: 1,
  tiebreak: [],
  selfScoring: false,
};

export const DEFAULT_STATS_SETTINGS: StatsSettings = {
  minGames: 1,
  combined: false,
  awardsPublic: false,
  perGame: false,
};

export const DEFAULT_LOCALE_SETTINGS: LocaleSettings = {
  default: "uk",
};

/** The compiled-in game config, minus the half the app derives. */
export const DEFAULT_FORMAT: FormatSettings = {
  clockMode: DEFAULT_CONFIG.clockMode,
  scoreTarget: DEFAULT_CONFIG.scoreTarget,
  timeoutsPerHalf: DEFAULT_CONFIG.timeoutsPerHalf,
};

export const DEFAULT_OVERLAY_SETTINGS: OverlaySettings = {
  finalCardSec: 60,
  pregameLeadMin: 2,
};

export const DEFAULT_SETUP_SETTINGS: SetupSettings = {
  singleDivision: false,
  durationsConfirmed: false,
};

export const DEFAULT_SETTINGS: TournamentSettings = {
  spirit: DEFAULT_SPIRIT_SETTINGS,
  stats: DEFAULT_STATS_SETTINGS,
  locale: DEFAULT_LOCALE_SETTINGS,
  slots: DEFAULT_SLOTS,
  format: DEFAULT_FORMAT,
  overlay: DEFAULT_OVERLAY_SETTINGS,
  setup: DEFAULT_SETUP_SETTINGS,
};

/**
 * A row as it ARRIVES, which is not the same shape as one that has been
 * through this function.
 *
 * Every section is optional, and inside `slots` every field is too: rows
 * written before a field existed are the normal case here, not an error, and
 * the settings PUT hands its slots section through unvalidated precisely so
 * the clamping below is the only place that decides what a duration may be.
 */
export interface StoredSettings extends Partial<Omit<TournamentSettings, "slots" | "overlay" | "setup">> {
  slots?: Partial<SlotSettings> | undefined;
  overlay?: Partial<OverlaySettings> | undefined;
  setup?: Partial<SetupSettings> | undefined;
}

/** Merge a stored row over the defaults. Rows written before a field existed
 *  must keep parsing, so every field falls back rather than being required. */
export function withDefaults(stored: StoredSettings | undefined): TournamentSettings {
  const spirit = stored?.spirit;
  const stats = stored?.stats;
  const locale = stored?.locale;
  const slots = stored?.slots;
  const format = stored?.format;
  /** A stored duration, clamped to something a game can actually be. Rows
   *  written before this section existed simply fall back. */
  const minutes = (v: unknown, fallback: number, min: number): number =>
    typeof v === "number" && Number.isFinite(v)
      ? Math.min(240, Math.max(min, Math.round(v)))
      : fallback;
  return {
    spirit: {
      enabled: spirit?.enabled ?? DEFAULT_SPIRIT_SETTINGS.enabled,
      visibility: isSpiritVisibility(spirit?.visibility)
        ? spirit.visibility
        : DEFAULT_SPIRIT_SETTINGS.visibility,
      minGames:
        typeof spirit?.minGames === "number" && Number.isFinite(spirit.minGames)
          ? Math.max(0, Math.round(spirit.minGames))
          : DEFAULT_SPIRIT_SETTINGS.minGames,
      tiebreak: Array.isArray(spirit?.tiebreak) ? spirit.tiebreak.filter(isSpiritTiebreak) : [],
      selfScoring: spirit?.selfScoring ?? DEFAULT_SPIRIT_SETTINGS.selfScoring,
      ...(typeof spirit?.deadline === "string" && spirit.deadline !== ""
        ? { deadline: spirit.deadline }
        : {}),
    },
    stats: {
      minGames:
        typeof stats?.minGames === "number" && Number.isFinite(stats.minGames)
          ? Math.max(0, Math.round(stats.minGames))
          : DEFAULT_STATS_SETTINGS.minGames,
      combined: stats?.combined ?? DEFAULT_STATS_SETTINGS.combined,
      awardsPublic: stats?.awardsPublic ?? DEFAULT_STATS_SETTINGS.awardsPublic,
      perGame: stats?.perGame ?? DEFAULT_STATS_SETTINGS.perGame,
    },
    slots: {
      pool: minutes(slots?.pool, DEFAULT_SLOTS.pool, 10),
      bracket: minutes(slots?.bracket, DEFAULT_SLOTS.bracket, 10),
      final: minutes(slots?.final, DEFAULT_SLOTS.final, 10),
      // Zero turnaround is a real choice at a tight event: the pitch is free
      // the moment the last point lands.
      turnaround: minutes(slots?.turnaround, DEFAULT_SLOTS.turnaround, 0),
      rest: minutes(slots?.rest, DEFAULT_SLOTS.rest, 0),
      ...divisionSlots(slots?.byDivision),
    },
    locale: {
      default: isLocale(locale?.default) ? locale.default : DEFAULT_LOCALE_SETTINGS.default,
    },
    format: {
      clockMode:
        format?.clockMode === "countdown" || format?.clockMode === "countup"
          ? format.clockMode
          : DEFAULT_FORMAT.clockMode,
      // `null` is a real value here - a time-only format has no target - so
      // only a MISSING field falls back to the default.
      scoreTarget:
        format === undefined ? DEFAULT_FORMAT.scoreTarget : cleanTarget(format.scoreTarget),
      timeoutsPerHalf: cleanTimeouts(format?.timeoutsPerHalf) ?? DEFAULT_FORMAT.timeoutsPerHalf,
      ...(cleanCap(format?.softCapMin) !== undefined
        ? { softCapMin: cleanCap(format?.softCapMin) as number }
        : {}),
      ...(cleanCap(format?.hardCapMin) !== undefined
        ? { hardCapMin: cleanCap(format?.hardCapMin) as number }
        : {}),
      ...divisionFormats(format?.byDivision),
    },
    overlay: {
      // Zero is legal on both: a stream that wants no final card, or an intro
      // that only appears once the keeper starts the game.
      finalCardSec: bounded(
        stored?.overlay?.finalCardSec,
        DEFAULT_OVERLAY_SETTINGS.finalCardSec,
        0,
        600,
      ),
      pregameLeadMin: bounded(
        stored?.overlay?.pregameLeadMin,
        DEFAULT_OVERLAY_SETTINGS.pregameLeadMin,
        0,
        60,
      ),
    },
    setup: {
      singleDivision: stored?.setup?.singleDivision === true,
      durationsConfirmed: stored?.setup?.durationsConfirmed === true,
    },
  };
}

const bounded = (v: unknown, fallback: number, min: number, max: number): number =>
  typeof v === "number" && Number.isFinite(v) ? Math.min(max, Math.max(min, Math.round(v))) : fallback;

const cleanTarget = (v: unknown): number | null =>
  typeof v === "number" && Number.isFinite(v) ? Math.min(50, Math.max(1, Math.round(v))) : null;
const cleanTimeouts = (v: unknown): number | undefined =>
  typeof v === "number" && Number.isFinite(v)
    ? Math.min(9, Math.max(0, Math.round(v)))
    : undefined;
const cleanCap = (v: unknown): number | undefined =>
  typeof v === "number" && Number.isFinite(v)
    ? Math.min(240, Math.max(1, Math.round(v)))
    : undefined;

/**
 * Per-division format overrides, cleaned the way `divisionSlots` cleans the
 * lengths: junk entries are DROPPED whole (an entry that cannot say its
 * target and timeouts is not a format), the map is bounded, and nothing
 * survives when nothing is usable - a row that never used the feature stays
 * byte-for-byte what it was.
 */
function divisionFormats(raw: unknown): { byDivision?: Record<string, DivisionFormat> } {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) return {};
  const out: Record<string, DivisionFormat> = {};
  for (const [id, entry] of Object.entries(raw).slice(0, MAX_DIVISION_SLOTS)) {
    if (typeof entry !== "object" || entry === null || Array.isArray(entry)) continue;
    const v = entry as Record<string, unknown>;
    const timeouts = cleanTimeouts(v["timeoutsPerHalf"]);
    if (timeouts === undefined) continue;
    out[id] = {
      scoreTarget: cleanTarget(v["scoreTarget"]),
      timeoutsPerHalf: timeouts,
      ...(cleanCap(v["softCapMin"]) !== undefined
        ? { softCapMin: cleanCap(v["softCapMin"]) as number }
        : {}),
      ...(cleanCap(v["hardCapMin"]) !== undefined
        ? { hardCapMin: cleanCap(v["hardCapMin"]) as number }
        : {}),
    };
  }
  return Object.keys(out).length > 0 ? { byDivision: out } : {};
}

/**
 * The format a game in this division sets up with.
 *
 * The override REPLACES the four gameplay fields wholesale (it was seeded
 * from the effective values when the toggle went on, so its absences are
 * choices); clock mode always follows the tournament.
 */
export function formatFor(
  format: FormatSettings,
  divisionId: string | undefined,
): Omit<FormatSettings, "byDivision"> {
  const over = divisionId === undefined ? undefined : format.byDivision?.[divisionId];
  if (over === undefined) {
    const { byDivision: _all, ...base } = format;
    return base;
  }
  return { clockMode: format.clockMode, ...over };
}

/**
 * The format ONE GAME sets up with: its own override when it carries one,
 * otherwise its division's (which may be the tournament's). Same rule as
 * `formatFor`: an override replaces the four gameplay fields wholesale and
 * clock mode always follows the tournament. `override.minutes` is not in
 * here - it is a duration, and `playMinutes` in `game-length.ts` reads it.
 */
export function formatForGame(
  format: FormatSettings,
  game: Pick<ScheduledGame, "divisionId" | "override">,
): Omit<FormatSettings, "byDivision"> {
  const base = formatFor(format, game.divisionId);
  const own = game.override;
  if (own === undefined) return base;
  return {
    clockMode: base.clockMode,
    scoreTarget: own.scoreTarget,
    timeoutsPerHalf: own.timeoutsPerHalf,
    ...(own.softCapMin !== undefined ? { softCapMin: own.softCapMin } : {}),
    ...(own.hardCapMin !== undefined ? { hardCapMin: own.hardCapMin } : {}),
  };
}

/** As many divisions as an event could plausibly run. A bound rather than a
 *  rule: the settings row is read on every board poll and must not be able to
 *  grow without one. */
const MAX_DIVISION_SLOTS = 32;

/**
 * Per-division overrides, cleaned.
 *
 * Three things happen here and each one keeps the stored row honest. A field
 * that is not a usable number is DROPPED rather than defaulted, because absent
 * is what "inherit" is spelled as and a default would freeze the division at
 * today's tournament-wide value. A division left with no overrides at all is
 * dropped whole, so clearing the last one leaves no empty object behind to
 * read as "this division is configured". And the result is omitted entirely
 * when nothing survives, so a row that never used the feature is byte-for-byte
 * what it was before the feature existed.
 */
function divisionSlots(raw: unknown): { byDivision?: Record<string, DivisionSlots> } {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) return {};
  const clamp = (v: unknown): number | undefined =>
    typeof v === "number" && Number.isFinite(v)
      ? Math.min(240, Math.max(10, Math.round(v)))
      : undefined;

  const out: Record<string, DivisionSlots> = {};
  for (const [id, value] of Object.entries(raw as Record<string, unknown>)) {
    if (Object.keys(out).length >= MAX_DIVISION_SLOTS) break;
    if (id === "" || typeof value !== "object" || value === null) continue;
    const v = value as Record<string, unknown>;
    const one: DivisionSlots = {
      ...(clamp(v["pool"]) !== undefined ? { pool: clamp(v["pool"]) as number } : {}),
      ...(clamp(v["bracket"]) !== undefined ? { bracket: clamp(v["bracket"]) as number } : {}),
      ...(clamp(v["final"]) !== undefined ? { final: clamp(v["final"]) as number } : {}),
    };
    if (Object.keys(one).length > 0) out[id] = one;
  }
  return Object.keys(out).length > 0 ? { byDivision: out } : {};
}

/**
 * What `GET /api/settings` serves. Every field is listed by hand: the day a
 * private setting is added, this function is where it stops.
 */
export function publicSettings(s: TournamentSettings): TournamentSettings {
  return {
    spirit: {
      enabled: s.spirit.enabled,
      visibility: s.spirit.visibility,
      minGames: s.spirit.minGames,
      tiebreak: s.spirit.tiebreak,
      selfScoring: s.spirit.selfScoring,
      ...(s.spirit.deadline !== undefined ? { deadline: s.spirit.deadline } : {}),
    },
    stats: {
      minGames: s.stats.minGames,
      combined: s.stats.combined,
      awardsPublic: s.stats.awardsPublic,
      perGame: s.stats.perGame,
    },
    // Public by nature: the board has to know which language to open in before
    // anybody has tapped anything.
    // Public like the rest: how long a game lasts is printed on the schedule
    // and read by the board. Nothing here is a credential.
    slots: { ...s.slots },
    // The setup dialog that reads these prefills sits behind a scorekeeper
    // link, but the numbers themselves are the tournament's public playing
    // format - the same ones the schedule prints.
    format: { ...s.format },
    locale: { default: s.locale.default },
    // Public by necessity: the OBS overlay is an unauthenticated page and its
    // auto scene runs on these two timers.
    overlay: { ...s.overlay },
    // Two booleans about how the event was set up. Nothing private, and the
    // admin's checklist reads the same public row every other panel does.
    setup: { ...s.setup },
  };
}
